import * as Yup from 'yup';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Header,
  Icon,
  Segment,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Message,
  Dimmer,
  Loader,
  Confirm,
  Dropdown,
  Grid,
  TextArea,
  Checkbox,
  Label,
  Popup,
  Divider
} from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Formik } from 'formik';

import { useStore } from '../../app/stores/store';
import { CompanySportVenue } from '../../app/models/CompanySportVenue';
import { CompanySportVenueAsset } from '../../app/models/CompanySportVenueAsset';
import { Sport } from '../../app/models/Sport';
import { sportOptions, createSportOptions } from '../../app/common/options/sportOptions';
import WorkingHours, { WorkingHoursData } from './WorkingHours';
import { getDetailedWorkingHours, getWorkingHoursSummary, isCurrentlyOpen } from './workingHoursHelper';
import PartitionRulesSection from './PartitionRulesSection';
import PricingRulesSection from './PricingRulesSection';
import DiscountRulesSection from './DiscountRulesSection';

// Define the props interface for AssetForm
interface AssetFormProps {
  asset: CompanySportVenueAsset;
  venues: CompanySportVenue[];
  onClose: () => void;
  onSubmit: (asset: CompanySportVenueAsset) => void;
  isNewAsset: boolean;
}

const AssetForm = observer(({ asset, venues, onClose, onSubmit, isNewAsset }: AssetFormProps) => {
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    name: Yup.string().required(t('validation.assetNameRequired')),
    description: Yup.string().required(t('validation.descriptionRequired')),
    companySportVenueID: Yup.string().required(t('validation.venueRequired')),
    sports: Yup.array()
      .min(1, t('validation.atLeastOneSportRequired'))
      .required(t('validation.sportsSelectionRequired')),

    // Capacity validation
    totalCapacityUnits: Yup.number()
      .min(1, t('validation.capacityMustBePositive'))
      .required(t('validation.totalCapacityRequired')),

    minBookableUnits: Yup.number()
      .min(1, t('validation.minBookableMustBePositive'))
      .max(Yup.ref('totalCapacityUnits'), t('validation.minBookableExceedsTotal'))
      .nullable(),

    // Validate at least one partition rule exists
    partitionRules: Yup.array()
      .min(1, t('validation.atLeastOnePartitionRequired'))
      .required(),

    // Pricing coverage is enforced at submit time via getPricingWarnings()
    // below, NOT here. Putting it in the Yup schema fires on every
    // keystroke / delete and blocks mid-edit workflows (e.g. adding a
    // partition before adding its price, or deleting a price before
    // deleting its partition).
    pricingRules: Yup.array()
  });

  // Convert venues to venue options for the dropdown
  const venueSelectOptions = venues.map(venue => ({
    key: venue.id,
    text: venue.name,
    value: venue.id
  }));

  // Get localized sport options
  const localizedSportOptions = createSportOptions(t, sportOptions);

  // Convert selected sports to react-select format
  const getSelectedSportsOptions = (sports?: Sport[]) => {
    if (!sports || sports.length === 0) return [];
    return sports.map(sport => ({
      value: sport.id,
      label:
        localizedSportOptions.find(option => option.value === sport.id)?.label ||
        t('common.unknownSport')
    }));
  };

  // Convert react-select options back to Sport objects
  const convertOptionsToSports = (options: any[]): Sport[] => {
    return options.map(option => ({
      id: option.value,
      createdAt: null,
      modifiedAt: null,
      modifiedBy: null,
      isDeleted: null,
      sportName: option.label,
      defaultParticipantsNumber: null,
      sortIndex: null,
      selfReportedLevel: null,
    }));
  };

  // Returns capacity sizes that are active but missing a pricing rule.
  // Used to block submission and render a soft warning — NOT part of
  // the Yup schema so it never fires during normal editing.
  const getPricingWarnings = (vals: CompanySportVenueAsset): number[] => {
    const activeCapacities = (vals.partitionRules || [])
      .filter(pr => pr.isActive)
      .map(pr => pr.capacityUnits);
    const pricedCapacities = (vals.pricingRules || [])
      .filter(pr => pr.isActive)
      .map(pr => pr.capacityUnits);
    return activeCapacities.filter(cap => !pricedCapacities.includes(cap));
  };


  return (
    <Formik
      initialValues={{
        ...asset,
        sports: asset.sports || [],
        totalCapacityUnits: asset.totalCapacityUnits || 60,
        minBookableUnits: asset.minBookableUnits ?? undefined,
        isDivisible: asset.isDivisible !== undefined ? asset.isDivisible : true,
        partitionRules: asset.partitionRules || [],
        pricingRules: asset.pricingRules || [],
        discountRules: asset.discountRules || []
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        if (getPricingWarnings(values).length > 0) return;
        onSubmit(values);
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        setFieldValue,
        setValues,   // ← used for atomic multi-field updates
        isValid,
        isSubmitting,
        dirty
      }) => {

        const handleWorkingHoursChange = useCallback(
          (workingHours: WorkingHoursData[]) => {
            const sportVenueAssetWorkingHours = workingHours.map(wh => ({
              id: wh.id,
              companySportVenueAssetID:
                values.id || '00000000-0000-0000-0000-000000000000',
              dayOfWeek: wh.dayOfWeek,
              openTime: wh.openTime,
              closeTime: wh.closeTime,
              breakStart: wh.breakStart,
              breakEnd: wh.breakEnd,
              isClosed: wh.isClosed
            }));
            setFieldValue('sportVenueAssetWorkingHours', sportVenueAssetWorkingHours);
          },
          [setFieldValue, values.id]
        );

        // ─────────────────────────────────────────────────────────────────
        // Partition rule handlers
        // Deleting a partition rule also removes all pricing rules that
        // reference the same capacityUnits. Both collections are updated
        // in one setValues() call so Yup never sees an intermediate state
        // where a partition exists without pricing or vice-versa.
        // ─────────────────────────────────────────────────────────────────
        const handlePartitionAdd = useCallback(
          (rule: any) => {
            setFieldValue('partitionRules', [
              ...(values.partitionRules || []),
              rule
            ]);
          },
          [setFieldValue, values.partitionRules]
        );

        const handlePartitionDelete = useCallback(
          (ruleId: string) => {
            // New (unsaved) rules have no id — PartitionRulesSection falls back
            // to passing String(capacityUnits) as the ruleId in that case.
            // So we match by id first, then by capacityUnits string fallback.
            const deletedRule = (values.partitionRules || []).find(
              r => (r.id ? r.id === ruleId : String(r.capacityUnits) === ruleId)
            );

            const newPartitionRules = (values.partitionRules || []).filter(
              r => !(r.id ? r.id === ruleId : String(r.capacityUnits) === ruleId)
            );

            // Remove ALL pricing rules tied to this partition's capacity so the
            // pricing warning never fires a false positive.
            const newPricingRules = deletedRule
              ? (values.pricingRules || []).filter(
                pr => pr.capacityUnits !== deletedRule.capacityUnits
              )
              : values.pricingRules || [];

            // Single atomic update — validation runs once against the final state
            setValues(
              prev => ({
                ...prev,
                partitionRules: newPartitionRules,
                pricingRules: newPricingRules
              }),
              true
            );
          },
          [setValues, values.partitionRules, values.pricingRules]
        );

        // ─────────────────────────────────────────────────────────────────
        // Pricing rule handlers
        // ─────────────────────────────────────────────────────────────────
        const handlePricingAdd = useCallback(
          (rule: any) => {
            setFieldValue('pricingRules', [
              ...(values.pricingRules || []),
              rule
            ]);
          },
          [setFieldValue, values.pricingRules]
        );

        const handlePricingUpdate = useCallback(
          (rule: any) => {
            setFieldValue(
              'pricingRules',
              (values.pricingRules || []).map(r => (r.id === rule.id ? rule : r))
            );
          },
          [setFieldValue, values.pricingRules]
        );

        const handlePricingDelete = useCallback(
          (ruleId?: string, capacityUnits?: number) => {
            // ruleId is the real DB id for saved rules, undefined for new ones.
            // PricingRulesSection always passes capacityUnits as the second arg,
            // so we use that as the fallback key for unsaved rules.
            setFieldValue(
              'pricingRules',
              (values.pricingRules || []).filter(r => {
                if (ruleId && r.id) return r.id !== ruleId;
                return r.capacityUnits !== capacityUnits;
              })
            );
          },
          [setFieldValue, values.pricingRules]
        );

        // ─────────────────────────────────────────────────────────────────
        // Discount rule handlers
        // ─────────────────────────────────────────────────────────────────
        const handleDiscountAdd = useCallback(
          (rule: any) => {
            setFieldValue('discountRules', [
              ...(values.discountRules || []),
              rule
            ]);
          },
          [setFieldValue, values.discountRules]
        );

        const handleDiscountUpdate = useCallback(
          (rule: any) => {
            setFieldValue(
              'discountRules',
              (values.discountRules || []).map(r => (r.id === rule.id ? rule : r))
            );
          },
          [setFieldValue, values.discountRules]
        );

        const handleDiscountDelete = useCallback(
          (ruleId?: string, _description?: string) => {
            if (!ruleId) return;
            setFieldValue(
              'discountRules',
              (values.discountRules || []).filter(r => r.id !== ruleId)
            );
          },
          [setFieldValue, values.discountRules]
        );

        return (
          <Form onSubmit={handleSubmit} error>

            <Form.Field>
              <label>{t('assetForm.assetName')}</label>
              <Input
                name="name"
                placeholder={t('assetForm.assetNamePlaceholder')}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && !!errors.name}
              />
              {touched.name && errors.name && (
                <Message error content={errors.name} />
              )}
            </Form.Field>

            <Form.Field>
              <label>{t('assetForm.venue')}</label>
              <Dropdown
                placeholder={t('assetForm.selectVenue')}
                fluid
                selection
                options={venueSelectOptions}
                value={values.companySportVenueID || ''}
                onChange={(_, data) =>
                  setFieldValue('companySportVenueID', data.value)
                }
                error={touched.companySportVenueID && !!errors.companySportVenueID}
              />
              {touched.companySportVenueID && errors.companySportVenueID && (
                <Message error content={errors.companySportVenueID} />
              )}
            </Form.Field>

            <Form.Field>
              <label>{t('assetForm.sportsAvailable')} *</label>
              <Select
                isMulti
                options={localizedSportOptions}
                value={getSelectedSportsOptions(values.sports)}
                onChange={selectedOptions => {
                  const sports = convertOptionsToSports([
                    ...(selectedOptions || [])
                  ]);
                  setFieldValue('sports', sports);
                }}
                placeholder={t('assetForm.selectSportsPlaceholder')}
                className="react-select-container"
                classNamePrefix="react-select"
              />
              {touched.sports && errors.sports && (
                <Message
                  error
                  content={
                    typeof errors.sports === 'string'
                      ? errors.sports
                      : t('validation.selectAtLeastOneSport')
                  }
                />
              )}
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                {t('assetForm.selectSportsHelp')}
              </div>
            </Form.Field>

            <Form.Field>
              <label>{t('assetForm.description')}</label>
              <TextArea
                name="description"
                placeholder={t('assetForm.descriptionPlaceholder')}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.description && errors.description && (
                <Message error content={errors.description} />
              )}
            </Form.Field>

            <Form.Group stackable>
              <Form.Field>
                <Checkbox
                  name="isBookable"
                  label={t('assetForm.isBookable')}
                  checked={values.isBookable}
                  onChange={(_, data) =>
                    setFieldValue('isBookable', data.checked)
                  }
                />
              </Form.Field>
              <Form.Field>
                <Checkbox
                  name="isActive"
                  label={t('assetForm.isActive')}
                  checked={values.isActive}
                  onChange={(_, data) =>
                    setFieldValue('isActive', data.checked)
                  }
                />
              </Form.Field>
            </Form.Group>

            {/* Capacity Management Section */}
            <Header as="h4" dividing style={{ marginTop: '30px' }}>
              <Icon name="sliders horizontal" />
              {t('assetForm.capacityManagement')}
            </Header>

            <Message info size="small">
              <Message.Header>{t('assetForm.capacityInfo.title')}</Message.Header>
              <p>{t('assetForm.capacityInfo.description')}</p>
            </Message>

            <Form.Group widths="equal">
              <Form.Field>
                <label>{t('assetForm.totalCapacityUnits')}</label>
                <Input
                  type="number"
                  name="totalCapacityUnits"
                  value={values.totalCapacityUnits}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min="1"
                  error={
                    touched.totalCapacityUnits && !!errors.totalCapacityUnits
                  }
                />
                {touched.totalCapacityUnits && errors.totalCapacityUnits && (
                  <Message error content={errors.totalCapacityUnits} />
                )}
                <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                  {t('assetForm.totalCapacityHelp')}
                </div>
              </Form.Field>

              <Form.Field>
                <label>{t('assetForm.minBookableUnits')}</label>
                <Input
                  type="number"
                  name="minBookableUnits"
                  value={values.minBookableUnits || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min="1"
                  max={values.totalCapacityUnits}
                  placeholder={t('assetForm.optional')}
                />
                {touched.minBookableUnits && errors.minBookableUnits && (
                  <Message error content={errors.minBookableUnits} />
                )}
                <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                  {t('assetForm.minBookableHelp')}
                </div>
              </Form.Field>
            </Form.Group>

            <Form.Field>
              <Checkbox
                name="isDivisible"
                label={t('assetForm.isDivisible')}
                checked={values.isDivisible}
                onChange={(_, data) =>
                  setFieldValue('isDivisible', data.checked)
                }
              />
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                {t('assetForm.isDivisibleHelp')}
              </div>
            </Form.Field>

            {/* Partition Rules */}
            <PartitionRulesSection
              assetId={values.id}
              totalCapacity={values.totalCapacityUnits}
              partitionRules={values.partitionRules || []}
              onAdd={handlePartitionAdd}
              onDelete={handlePartitionDelete}
              isNewAsset={isNewAsset}
            />

            {/* Pricing Rules */}
            <PricingRulesSection
              assetId={values.id}
              partitionRules={values.partitionRules || []}
              pricingRules={values.pricingRules || []}
              onAdd={handlePricingAdd}
              onUpdate={handlePricingUpdate}
              onDelete={handlePricingDelete}
              isNewAsset={isNewAsset}
            />

            {/* Discount Rules */}
            <DiscountRulesSection
              assetId={values.id}
              discountRules={values.discountRules || []}
              onAdd={handleDiscountAdd}
              onUpdate={handleDiscountUpdate}
              onDelete={handleDiscountDelete}
              isNewAsset={isNewAsset}
            />

            {/* Working Hours */}
            <Divider />
            <WorkingHours
              title={t('assetForm.assetWorkingHours')}
              workingHours={
                values.sportVenueAssetWorkingHours?.map(wh => ({
                  id: wh.id,
                  dayOfWeek: wh.dayOfWeek,
                  openTime: wh.openTime,
                  closeTime: wh.closeTime,
                  breakStart: wh.breakStart,
                  breakEnd: wh.breakEnd,
                  isClosed: wh.isClosed
                })) || []
              }
              onChange={handleWorkingHoursChange}
              parentId={values.id || '00000000-0000-0000-0000-000000000000'}
            />

            {/* Soft pricing warning — visible during editing but never blocks deletion */}
            {(() => {
              const unpricedCaps = getPricingWarnings(values);
              const hasNoPricingAtAll = !values.pricingRules || values.pricingRules.length === 0;
              const hasPartitions = values.partitionRules && values.partitionRules.filter(r => r.isActive).length > 0;

              return (
                <>
                  {/* Hard error: no pricing rules at all but partitions exist */}
                  {hasNoPricingAtAll && hasPartitions && (
                    <Message error>
                      <Message.Header>{t('assetForm.validation.noPricingRules.title')}</Message.Header>
                      <p>{t('assetForm.validation.noPricingRules.description')}</p>
                    </Message>
                  )}

                  {/* Soft warning: some partitions are missing pricing */}
                  {!hasNoPricingAtAll && unpricedCaps.length > 0 && (
                    <Message warning>
                      <Message.Header>{t('venues.validation.pricingForAllPartitions')}</Message.Header>
                      <p>
                        {t('assetForm.missingPricingFor')}:{' '}
                        {unpricedCaps.map(cap => (
                          <Label key={cap} size='mini' color='orange' style={{ margin: '2px' }}>
                            {cap} {t('capacity.units')}
                          </Label>
                        ))}
                      </p>
                    </Message>
                  )}
                </>
              );
            })()}

            {/* Validation Summary — only shown after the user has interacted */}
            {!isValid && dirty && Object.keys(errors).length > 0 && (
              <Message error>
                <Message.Header>{t('validation.pleaseFixErrors')}</Message.Header>
                <Message.List>
                  {Object.entries(errors).map(([key, value]) => (
                    <Message.Item key={key}>
                      {typeof value === 'string' ? value : JSON.stringify(value)}
                    </Message.Item>
                  ))}
                </Message.List>
              </Message>
            )}

            <Button.Group fluid stackable style={{ marginTop: '20px' }}>
              <Button type="button" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button.Or />
              <Button
                positive
                type="submit"
                disabled={!isValid || isSubmitting || getPricingWarnings(values).length > 0}
                loading={isSubmitting}
              >
                {t('common.save')}
              </Button>
            </Button.Group>
          </Form>
        );
      }}
    </Formik>
  );
});

export default observer(function VenueAssets() {
  const { t } = useTranslation();
  const {
    companyStore,
    companyVenueStore,
    companySportVenueAssetStore,
    userStore
  } = useStore();
  const { loadAllAssets, assetRegistry, loading, loadingInitial } =
    companySportVenueAssetStore;
  const { companyRegistry, loadUserCompanies } = companyStore;
  const { venueRegistry, loadAllVenues } = companyVenueStore;
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] =
    useState<CompanySportVenueAsset | null>(null);
  const [isNewAsset, setIsNewAsset] = useState(true);
  const [filterByCompany, setFilterByCompany] = useState<string | null>(null);
  const [filterByVenue, setFilterByVenue] = useState<string | null>(null);

  useEffect(() => {
    if (userStore.user) {
      loadAllAssets();
      loadAllVenues();
      loadUserCompanies(userStore.user.userID);
    }
  }, [loadAllAssets, loadAllVenues, loadUserCompanies, userStore.user]);

  const handleCreateAsset = () => {
    setIsNewAsset(true);
    setSelectedAsset({
      id: '',
      name: '',
      description: '',
      companySportVenueID: '',
      totalCapacityUnits: 60,
      minBookableUnits: undefined,
      isDivisible: true,
      isBookable: true,
      isActive: true,
      sports: [],
      sportVenueAssetWorkingHours: [],
      partitionRules: [],
      pricingRules: [],
      discountRules: []
    });
    setModalOpen(true);
  };

  const handleEditAsset = (asset: CompanySportVenueAsset) => {
    setIsNewAsset(false);
    setSelectedAsset(asset);
    setModalOpen(true);
  };

  const handleDeleteClick = (asset: CompanySportVenueAsset) => {
    setSelectedAsset(asset);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedAsset) {
      await companySportVenueAssetStore.deleteAsset(selectedAsset.id);
      setDeleteConfirmOpen(false);
    }
  };

  const handleSubmit = async (asset: CompanySportVenueAsset) => {
    try {
      if (isNewAsset) {
        await companySportVenueAssetStore.createAsset(asset);
        await loadAllAssets();
      } else {
        await companySportVenueAssetStore.updateAsset(asset);
      }
      setModalOpen(false);
    } catch (error) {
      console.error('Error submitting asset:', error);
    }
  };

  // ── Filtering ──────────────────────────────────────────────────────────
  const allAssets = Array.from(assetRegistry.values());

  let filteredAssets = allAssets;

  if (filterByCompany) {
    const companyVenueIds = Array.from(venueRegistry.values())
      .filter(venue => venue.companyID === filterByCompany)
      .map(venue => venue.id);
    filteredAssets = filteredAssets.filter(asset =>
      companyVenueIds.includes(asset.companySportVenueID)
    );
  }

  if (filterByVenue) {
    filteredAssets = filteredAssets.filter(
      asset => asset.companySportVenueID === filterByVenue
    );
  }

  // ── Dropdown options ───────────────────────────────────────────────────
  const companySelectOptions = [
    { value: null, label: t('venueAssets.allCompanies') },
    ...Array.from(companyRegistry.values()).map(company => ({
      value: company.id,
      label: company.companyName
    }))
  ];

  const getVenueSelectOptions = () => {
    let venues = Array.from(venueRegistry.values());
    if (filterByCompany) {
      venues = venues.filter(venue => venue.companyID === filterByCompany);
    }
    return [
      { value: null, label: t('venueAssets.allVenues') },
      ...venues.map(venue => ({ value: venue.id, label: venue.name }))
    ];
  };

  const allVenueOptions = Array.from(venueRegistry.values());

  // ── Helpers ────────────────────────────────────────────────────────────
  const getVenueName = (venueId: string) => {
    const venue = venueRegistry.get(venueId);
    return venue ? venue.name : t('common.unknownVenue');
  };

  const getCompanyNameByVenue = (venueId: string) => {
    const venue = venueRegistry.get(venueId);
    if (!venue || !venue.companyID) return t('common.notAssigned');
    const company = companyRegistry.get(venue.companyID);
    return company ? company.companyName : t('common.unknownCompany');
  };

  const getLocalizedSportName = (sportId: string, sportName?: any) => {
    const sportOption = sportOptions.find(option => option.value === sportId);
    if (sportOption) {
      const translationKey = `sports.${sportOption.label.toLowerCase()}`;
      const translatedName = t(translationKey);
      if (translatedName !== translationKey) return translatedName;
    }
    if (sportName) {
      const translationKey = `sports.${sportName.toLowerCase()}`;
      const translatedName = t(translationKey);
      if (translatedName !== translationKey) return translatedName;
      return sportName;
    }
    return t('common.unknownSport');
  };

  const getSportsDisplay = (sports?: Sport[]) => {
    if (!sports || sports.length === 0) return t('venueAssets.noSportsAssigned');
    return sports
      .map(sport => getLocalizedSportName(sport.id, sport.sportName))
      .filter(Boolean)
      .join(', ');
  };

  // Reset venue filter when company filter changes
  useEffect(() => {
    if (filterByCompany) setFilterByVenue(null);
  }, [filterByCompany]);

  if (loadingInitial) {
    return (
      <Segment>
        <Dimmer active inverted>
          <Loader inverted content={t('venueAssets.loadingVenueAssets')} />
        </Dimmer>
      </Segment>
    );
  }

  return (
    <Container fluid>
      <style>{`
        @media only screen and (max-width: 767px) {
          .ui.container.fluid {
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding-left: 0.5em !important;
            padding-right: 0.5em !important;
          }
          .ui.segment.clearing { padding: 0.5em !important; }
          .ui.table.mobile-card { border: none !important; }
          .ui.table.mobile-card thead { display: none; }
          .ui.table.mobile-card tbody { display: block; }
          .ui.table.mobile-card tr {
            display: block;
            margin-bottom: 1em;
            border: 1px solid rgba(34,36,38,.15);
            border-radius: .28571429rem;
            box-shadow: 0 1px 2px 0 rgba(34,36,38,.15);
          }
          .ui.table.mobile-card td {
            display: flex;
            flex-direction: column;
            padding: 0.5em !important;
            border: none !important;
            text-align: left !important;
          }
          .ui.table.mobile-card td:before {
            content: attr(data-label);
            font-weight: bold;
            margin-bottom: 0.3em;
            color: rgba(0,0,0,.87);
            font-size: 0.9em;
          }
          .ui.table.mobile-card td:empty { display: none; }
          .mobile-actions { display: flex; justify-content: space-around; margin-top: 0.5em; }
          .ui.header h2 { font-size: 1.3em !important; }
          .ui.modal { width: 95% !important; margin: 1em auto !important; }
          .ui.modal > .content { padding: 1em !important; }
        }
      `}</style>

      <Header as="h2">
        <Icon name="cubes" />
        <Header.Content>{t('venueAssets.title')}</Header.Content>
      </Header>

      <Segment clearing>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column mobile={16} tablet={8} computer={4}>
              <Select
                options={companySelectOptions}
                value={
                  companySelectOptions.find(
                    option => option.value === filterByCompany
                  ) || companySelectOptions[0]
                }
                onChange={selectedOption => {
                  setFilterByCompany(
                    selectedOption ? selectedOption.value : null
                  );
                }}
                placeholder={t('venueAssets.filterByCompany')}
                isClearable
              />
            </Grid.Column>
            <Grid.Column mobile={16} tablet={8} computer={4}>
              <Select
                options={getVenueSelectOptions()}
                value={
                  getVenueSelectOptions().find(
                    option => option.value === filterByVenue
                  ) || getVenueSelectOptions()[0]
                }
                onChange={selectedOption => {
                  setFilterByVenue(
                    selectedOption ? selectedOption.value : null
                  );
                }}
                placeholder={t('venueAssets.filterByVenue')}
                isClearable
                isDisabled={
                  !filterByCompany && getVenueSelectOptions().length <= 1
                }
              />
            </Grid.Column>
            <Grid.Column mobile={16} tablet={8} computer={4}>
              {filteredAssets.length === 0 && (
                <Message info size="mini">
                  <Message.Header>
                    {t('venueAssets.noAssetsFound')}
                  </Message.Header>
                  <p>{t('venueAssets.createNewAsset')}</p>
                </Message>
              )}
            </Grid.Column>
            <Grid.Column
              mobile={16}
              tablet={8}
              computer={4}
              textAlign="right"
            >
              <Button
                primary
                icon
                labelPosition="left"
                onClick={handleCreateAsset}
                disabled={allVenueOptions.length === 0}
                fluid
              >
                <Icon name="add" />
                {t('venueAssets.addAsset')}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {allVenueOptions.length === 0 && (
          <Message warning>
            <Message.Header>
              {t('venueAssets.noVenuesAvailable')}
            </Message.Header>
            <p>{t('venueAssets.noVenuesMessage')}</p>
          </Message>
        )}

        {filteredAssets.length > 0 && (
          <Table celled striped className="mobile-card">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {t('venueAssets.table.assetName')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('venueAssets.table.venue')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('venueAssets.table.company')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('venueAssets.table.sports')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('venueAssets.table.capacity')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('venueAssets.table.pricing')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('venueAssets.table.workingHours')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('venueAssets.table.description')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('venueAssets.table.status')}
                </Table.HeaderCell>
                <Table.HeaderCell width={2}>
                  {t('venueAssets.table.actions')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {filteredAssets.map(asset => (
                <Table.Row key={asset.id}>
                  <Table.Cell
                    data-label={t('venueAssets.table.assetName')}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap'
                      }}
                    >
                      <strong>{asset.name}</strong>
                      {isCurrentlyOpen(asset.sportVenueAssetWorkingHours) && (
                        <Label color="green" size="mini">
                          <Icon name="circle" />
                          {t('venueAssets.available')}
                        </Label>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell data-label={t('venueAssets.table.venue')}>
                    <p>{getVenueName(asset.companySportVenueID)}</p>
                  </Table.Cell>
                  <Table.Cell data-label={t('venueAssets.table.company')}>
                    <p>{getCompanyNameByVenue(asset.companySportVenueID)}</p>
                  </Table.Cell>
                  <Table.Cell data-label={t('venueAssets.table.sports')}>
                    <div style={{ wordBreak: 'break-word' }}>
                      {getSportsDisplay(asset.sports)}
                    </div>
                  </Table.Cell>
                  <Table.Cell data-label={t('venueAssets.table.capacity')}>
                    <Popup
                      trigger={
                        <Label color="blue" size="small">
                          <Icon name="grid layout" />
                          {asset.totalCapacityUnits} {t('capacity.units')}
                        </Label>
                      }
                      content={
                        <div>
                          <p>
                            <strong>
                              {t('assetForm.totalCapacityUnits')}:
                            </strong>{' '}
                            {asset.totalCapacityUnits}
                          </p>
                          {asset.minBookableUnits && (
                            <p>
                              <strong>
                                {t('assetForm.minBookableUnits')}:
                              </strong>{' '}
                              {asset.minBookableUnits}
                            </p>
                          )}
                          <p>
                            <strong>{t('assetForm.partitions')}:</strong>{' '}
                            {asset.partitionRules?.filter(r => r.isActive)
                              .length || 0}
                          </p>
                          {asset.partitionRules &&
                            asset.partitionRules.length > 0 && (
                              <div style={{ marginTop: '5px' }}>
                                {asset.partitionRules
                                  .filter(r => r.isActive)
                                  .sort(
                                    (a, b) =>
                                      b.capacityUnits - a.capacityUnits
                                  )
                                  .map(r => (
                                    <Label
                                      key={r.id}
                                      size="mini"
                                      style={{ margin: '2px' }}
                                    >
                                      {r.capacityUnits}
                                    </Label>
                                  ))}
                              </div>
                            )}
                        </div>
                      }
                      position="top center"
                      size="small"
                    />
                  </Table.Cell>
                  <Table.Cell data-label={t('venueAssets.table.pricing')}>
                    <Popup
                      trigger={
                        <Label color="green" size="small">
                          <Icon name="dollar sign" />
                          {asset.pricingRules?.filter(r => r.isActive)
                            .length || 0}{' '}
                          {t('venueAssets.table.rules')}
                        </Label>
                      }
                      content={
                        <div style={{ maxWidth: '250px' }}>
                          {asset.pricingRules &&
                            asset.pricingRules.length > 0 ? (
                            <Table basic="very" compact size="small">
                              <Table.Body>
                                {asset.pricingRules
                                  .filter(r => r.isActive)
                                  .sort(
                                    (a, b) =>
                                      b.capacityUnits - a.capacityUnits
                                  )
                                  .map(r => (
                                    <Table.Row key={r.id}>
                                      <Table.Cell>
                                        {r.capacityUnits} units
                                      </Table.Cell>
                                      <Table.Cell textAlign="right">
                                        <strong>
                                          ${r.pricePerHour.toFixed(2)}/hr
                                        </strong>
                                      </Table.Cell>
                                    </Table.Row>
                                  ))}
                              </Table.Body>
                            </Table>
                          ) : (
                            <p>{t('venueAssets.noPricingRules')}</p>
                          )}
                        </div>
                      }
                      position="top center"
                      size="small"
                    />
                  </Table.Cell>
                  <Table.Cell
                    data-label={t('venueAssets.table.workingHours')}
                  >
                    <Popup
                      trigger={
                        <div style={{ cursor: 'pointer' }}>
                          <Icon name="clock outline" />
                          {getWorkingHoursSummary(
                            asset.sportVenueAssetWorkingHours
                          )}
                        </div>
                      }
                      content={
                        <div
                          style={{ whiteSpace: 'pre-line', fontSize: '12px' }}
                        >
                          <strong>
                            {t('venueAssets.assetWorkingHours')}:
                          </strong>
                          <br />
                          {getDetailedWorkingHours(
                            asset.sportVenueAssetWorkingHours
                          )}
                        </div>
                      }
                      position="top center"
                      size="small"
                      inverted
                    />
                  </Table.Cell>
                  <Table.Cell
                    data-label={t('venueAssets.table.description')}
                  >
                    <p>
                      {asset.description && asset.description.length > 50
                        ? `${asset.description.substring(0, 50)}...`
                        : asset.description}
                    </p>
                  </Table.Cell>
                  <Table.Cell data-label={t('venueAssets.table.status')}>
                    <Label.Group>
                      <Label
                        color={asset.isActive ? 'green' : 'grey'}
                        size="mini"
                      >
                        {asset.isActive
                          ? t('common.active')
                          : t('common.inactive')}
                      </Label>
                      <Label
                        color={asset.isBookable ? 'blue' : 'orange'}
                        size="mini"
                      >
                        {asset.isBookable
                          ? t('common.bookable')
                          : t('common.notBookable')}
                      </Label>
                    </Label.Group>
                  </Table.Cell>
                  <Table.Cell data-label={t('venueAssets.table.actions')}>
                    <p>
                      <Button.Group size="mini" className="mobile-actions">
                        <Button
                          icon="edit"
                          color="blue"
                          onClick={() => handleEditAsset(asset)}
                        />
                        <Button
                          icon="trash"
                          color="red"
                          onClick={() => handleDeleteClick(asset)}
                        />
                      </Button.Group>
                    </p>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}

        {filteredAssets.length > 0 && (
          <Segment basic textAlign="center">
            <Label basic>
              {t('venueAssets.total')}: {filteredAssets.length}{' '}
              {filteredAssets.length !== 1
                ? t('venueAssets.assets')
                : t('venueAssets.asset')}
              {filterByCompany || filterByVenue
                ? ` (${t('venueAssets.filtered')})`
                : ''}
            </Label>
          </Segment>
        )}
      </Segment>

      {/* Create/Edit Asset Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="large"
      >
        <Modal.Header>
          {isNewAsset
            ? t('venueAssets.createNewAsset')
            : t('venueAssets.editAsset')}
        </Modal.Header>
        <Modal.Content scrolling>
          {selectedAsset && (
            <AssetForm
              asset={selectedAsset}
              venues={allVenueOptions}
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
              isNewAsset={isNewAsset}
            />
          )}
        </Modal.Content>
      </Modal>

      {/* Delete Confirmation */}
      <Confirm
        open={deleteConfirmOpen}
        header={t('venueAssets.deleteConfirm.header', {
          name: selectedAsset?.name
        })}
        content={t('venueAssets.deleteConfirm.content')}
        confirmButton={t('common.delete')}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Container>
  );
});